import React, { useState } from 'react';
import { TICKER_FALLBACK } from '../config/constants';
import './SignalCard.css';

const SIGNAL_MAP  = { 'Kaufen': 'buy', 'Verkaufen': 'sell', 'Halten': 'hold' };
const BADGE_LABEL = { buy: 'BUY', sell: 'SELL', hold: 'HOLD' };

function cleanTicker(ticker) {
  return ticker.replace('-USD', '').replace('=X', '').replace('^', '').split('.')[0];
}

function logoTicker(ticker) {
  if (ticker.includes('.')) return ticker;
  return ticker.replace('-USD', '').replace('=X', '').replace('^', '');
}

function SignalAvatar({ ticker }) {
  const [imgFailed, setImgFailed] = useState(false);
  const sym      = cleanTicker(ticker);
  const logoUrl  = `https://assets.parqet.com/logos/symbol/${logoTicker(ticker)}`;
  const fallback = TICKER_FALLBACK[ticker] || { bg: '#2563eb', color: '#fff' };

  if (!imgFailed) {
    return (
      <div className="sc-avatar sc-avatar--logo">
        <img
          src={logoUrl}
          alt={sym}
          onError={() => setImgFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
    );
  }

  return (
    <div
      className="sc-avatar sc-avatar--fallback"
      style={{ background: fallback.bg, color: fallback.color }}
    >
      {sym.slice(0, 2)}
    </div>
  );
}

function fmt(v, d = 1) {
  if (v == null || isNaN(v)) return '—';
  return `${v >= 0 ? '+' : ''}${Number(v).toFixed(d)}%`;
}

function fmtPrice(v) {
  if (v == null || isNaN(v)) return '—';
  return `€${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const RING_COLOR = { buy: '#22c55e', sell: '#ef4444', hold: '#a1a1aa' };

function ConfidenceRing({ value, cls }) {
  const r    = 18;
  const circ = 2 * Math.PI * r;
  const dash = Math.max(0, Math.min(value, 100)) / 100 * circ;
  const color = RING_COLOR[cls] || '#a1a1aa';
  const trackColor = cls === 'buy' ? 'rgba(34,197,94,0.15)' : cls === 'sell' ? 'rgba(239,68,68,0.15)' : '#ededf2';
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" className="sc-conf-ring" aria-label={`Konfidenz ${value}%`}>
      <circle cx="20" cy="20" r={r} fill="none" stroke={trackColor} strokeWidth="3"/>
      <circle cx="20" cy="20" r={r} fill="none" stroke={color} strokeWidth="3"
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform="rotate(-90 20 20)"/>
      <text x="20" y="24" textAnchor="middle" fontSize="10" fontWeight="700" fill={color}>{value}%</text>
    </svg>
  );
}

function SignalCard({ asset, onSelect, rebound }) {
  const cls  = SIGNAL_MAP[asset.signal] || 'hold';
  const c24  = asset.change   ?? 0;
  const c7d  = asset.change7d ?? 0;
  const conf = asset.confidence ?? 0;

  return (
    <article className={`sc-card ${cls}${rebound ? ' sc-card--rebound' : ''}`} onClick={() => onSelect?.(asset)}>
      {/* Badge absolute top-right */}
      <span className={`sc-badge ${cls}`}>{BADGE_LABEL[cls]}</span>

      {/* Top: Avatar + Identity */}
      <div className="sc-top">
        <SignalAvatar ticker={asset.ticker} />
        <div className="sc-identity">
          <div className="sc-ticker-row">
            <span className="sc-ticker">{asset.ticker}</span>
            <span className={`sc-ticker-chg ${c24 >= 0 ? 'pos' : 'neg'}`}>{fmt(c24)}</span>
          </div>
          <span className="sc-name">{asset.name}</span>
        </div>
      </div>

      {/* Stats: Price + 7d */}
      <div className="sc-stats">
        <div className="sc-stat">
          <span className="sc-stat-label">Kurs</span>
          <span className="sc-stat-value">{fmtPrice(asset.price)}</span>
        </div>
        <div className="sc-stat">
          <span className="sc-stat-label">7 Tage</span>
          <span className={`sc-stat-value ${c7d >= 0 ? 'pos' : 'neg'}`}>{fmt(c7d)}</span>
        </div>
      </div>

      {/* Reason */}
      <div className="sc-reason">{asset.reasons?.[0] ?? ''}</div>

      {/* Confidence ring */}
      <div className="sc-conf-row">
        <ConfidenceRing value={conf} cls={cls} />
        <span className="sc-conf-label">Signal-Stärke</span>
      </div>
    </article>
  );
}

export default SignalCard;
